/* ══════════════════════════════════════════════════════════════════════════
   LPL GLOBAL TRANSITION KIT · v1 — runtime (draft integration)
   Load after lpl-transition-kit.css, at the end of <body>, on every template.

   Two jobs, both TRANSITIONAL:
     A. Shared layout measurements (hero sizing and theme gutters).
        Homepage structure and controls are rendered by their sections.
     B. Role retint: one read of every element's computed colour, tagged by
        ROLE so the stylesheet can repaint hardcoded theme hexes.
        → Delete once every section assigns its colours from the tokens.

   Direction is LOCKED (Platinum · Precision · Airy). No switcher, no
   localStorage, no inline colour paint. Reads are batched before writes so
   a sweep costs one style recalculation, not one per element.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var d = document, root = d.documentElement;
  var desktopRoles = window.matchMedia('(min-width:750px)');
  var stylesReady = false, deferredDesktop = new Set(), mobileSquares = new Set();
  if (root.getAttribute('data-lpl-kit') !== 'transition-v1') root.setAttribute('data-lpl-kit', 'transition-v1');

  /* ── A · LAYOUT SURGERY ─────────────────────────────────────────────── */

  function setLayoutProperty(name, value) {
    if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
  }

  function headerHeight() {
    var h = 0;
    d.querySelectorAll('.shopify-section-group-header-group').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.display !== 'none') h += el.getBoundingClientRect().height;
    });
    return h;
  }

  /* Read scalar values before changing inherited custom properties. Computed
     styles are live: reading cs.maxWidth after writing --lpl-gutter previously
     recalculated the entire document, then headerHeight triggered it again. */
  function syncLayout() {
    var pw = d.querySelector('.page-width'), gutter, maxWidth;
    if (pw) {
      var cs = getComputedStyle(pw);
      gutter = cs.paddingLeft;
      maxWidth = cs.maxWidth === 'none' ? '1440px' : cs.maxWidth;
    }
    var heroHeight = 'calc(100svh - ' + Math.round(headerHeight()) + 'px)';
    var wrap = d.querySelector('.bs__slider--wrapp > .swiper-wrapper');
    if (pw) {
      setLayoutProperty('--lpl-gutter', gutter);
      setLayoutProperty('--lpl-maxw', maxWidth);
    }
    setLayoutProperty('--lpl-hero-h', heroHeight);
    if (desktopRoles.matches && wrap && wrap.style.getPropertyValue('height')) wrap.style.removeProperty('height');
  }

  /* Safety net for bands the theme still rounds at source: anything ≥92% of
     the viewport has no visible side edges, so its corners only leak paper.
     Narrower objects keep their radius. Delete once section CSS is fixed. */
  function squareFullBleed() {
    var vw = window.innerWidth, hits = [], phone = !desktopRoles.matches;
    if (phone) mobileSquares.forEach(function (el) {
      if (!el.isConnected || el.getBoundingClientRect().width < vw * 0.92) {
        el.removeAttribute('data-lpl-mobile-square'); mobileSquares.delete(el);
      }
    });
    d.querySelectorAll('.shopify-section div,.shopify-section section,footer').forEach(function (el) {
      if (el.closest(PRESERVE)) return;
      var cs = getComputedStyle(el);
      var rounded = phone
        ? ['TopLeft', 'TopRight', 'BottomLeft', 'BottomRight'].some(function (corner) { return cs['border' + corner + 'Radius'] !== '0px'; })
        : cs.borderTopLeftRadius !== '0px';
      if (rounded && el.getBoundingClientRect().width >= vw * 0.92) hits.push(el);
    });
    hits.forEach(function (el) {
      // A phone-only marker must not leave inline radius overrides on desktop.
      if (phone) { el.setAttribute('data-lpl-mobile-square', ''); mobileSquares.add(el); }
      else el.style.setProperty('border-radius', '0', 'important');
    });
  }

  /* ── B · ROLE RETINT ────────────────────────────────────────────────── */

  var SKIP = { HTML: 1, HEAD: 1, SCRIPT: 1, STYLE: 1, LINK: 1, META: 1, TITLE: 1, NOSCRIPT: 1, TEMPLATE: 1, IFRAME: 1 };
  var PRESERVE = '[data-lpl-native-page],.lpl-transition-page,[data-lpl-preserve],[data-lpl-preserve-color],.okeReviews,[data-oke-container],.oke-modal,.oke-sr,.oke-rs,#oke-star-symbols,.bs__stars--icons,.bwb__stars--icons,.cm__stars--icons,.lpl-listicle__stars,.lpl-benefits-stars,.rating-star,.bp__product-rating,[data-lpl-rating-stars],[class*="testimonials-stars-"],[class*="testimonials-slide-stars-"]';

  // Reject an entire protected subtree once instead of checking every descendant's
  // ancestors. Native pages, reviews and rating stars keep their source styling.
  function retintNodes(scope) {
    if (scope.closest && scope.closest(PRESERVE)) return [];
    var nodes = [], walker = d.createTreeWalker(scope, NodeFilter.SHOW_ELEMENT, {
      acceptNode: function (el) {
        return SKIP[el.tagName] || el.matches(PRESERVE) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });
    if (scope !== d.body && !SKIP[scope.tagName]) nodes.push(scope);
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
  }

  var COMMERCIAL = '.sticky-atc-bar__btn,.lpl-what-sticky__button,.bp__product-btn,.pt__link-btn--secondary,.ltr__link,.lpl-ac-offer__shop,.lpl-transition-buy,.lpl-palat__shop,.product-form__submit,.lpl-po__submit,.quick-add__submit,.shop-all__link,.lpl-ss__button,.lpl-ss__btn,.bs__link--primary,.cm__link--primary,.spw__cta,.pfl__cta,.pdp__btn-submit,.cart__checkout-button,.cart__btn-shop,.shopify-payment-button__button';
  var CONTROLS = '.button,.btn,.header__cta,.bs__link,.newsletter-form__button,.button--primary,[data-lpl-commercial],' + COMMERCIAL;
  // These radios and their indicators own state-dependent colours. Freezing
  // their initial paint into a role would keep a deselected card highlighted.
  var STATEFUL_COLORS = '.lpl-po__routine,.lpl-po__choice';

  // Mark the purchase action, never its A/B copy. Covers legacy and new sections.
  function markCommercial(scope) {
    var nodes = [].slice.call(scope.querySelectorAll('a,button,input[type="submit"]'));
    if (scope.matches && scope.matches('a,button,input[type="submit"]')) nodes.unshift(scope);
    nodes.forEach(function (el) {
      // The sticky component owns its surface; only its visual CTA is amber.
      if (el.closest('lpl-sticky-atc')) return;
      var label = (el.textContent || el.value || '').replace(/\s+/g, ' ').trim();
      var experiment = el.querySelector('[data-lpl-ab="pdp_main"],[data-lpl-ab="pdp_sticky"],[data-lpl-ab="site_sticky"],[data-lpl-ab="seo_cta"]');
      if (el.matches(COMMERCIAL) || experiment || /\bshop now\b|\badd to cart\b|\bbuy now\b/i.test(label)) {
        if (el.getAttribute('data-lpl-commercial') !== '1') el.setAttribute('data-lpl-commercial', '1');
      }
    });
  }
  /* legacy cream hexes the scheme remap already sends to paper */
  var PAPER = { '236,235,222': 1, '251,250,246': 1, '243,243,243': 1 };

  function parse(str) {
    var m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(str || '');
    if (!m) return null;
    var a = m[4] === undefined ? 1 : parseFloat(m[4]);
    if (a < 0.6) return null;                       // translucent scrims stay put
    var r = +m[1] / 255, g = +m[2] / 255, b = +m[3] / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, dd = mx - mn;
    var s = dd === 0 ? 0 : dd / (1 - Math.abs(2 * l - 1)), h = 0;
    if (dd !== 0) {
      if (mx === r) h = 60 * ((((g - b) / dd) % 6 + 6) % 6);
      else if (mx === g) h = 60 * (((b - r) / dd) + 2);
      else h = 60 * (((r - g) / dd) + 4);
    }
    return { h: h, s: s, l: l, key: m[1] + ',' + m[2] + ',' + m[3] };
  }

  function groundRole(str) {
    var c = parse(str); if (!c) return null;
    if (c.s > 0.5 && c.h >= 30 && c.h <= 105 && c.l > 0.34) return 'acc';      // lime/amber walls → never a wall
    if (c.s > 0.09 && c.h >= 78 && c.h <= 190) {                               // brand greens
      if (c.l < 0.15) return 'slab';
      if (c.l < 0.45) return 'chapter';
      if (c.l > 0.86) return 'band';
      return null;
    }
    if (PAPER[c.key]) return 'paper';
    if (c.s <= 0.40 && c.l > 0.80 && c.l < 0.995 && c.h >= 18 && c.h <= 72) return 'band'; // other creams/sand → bone
    if (c.s < 0.14 && c.l < 0.13) return 'slab';                               // near-black → deep forest
    return null;
  }

  function inkRole(str) {
    var c = parse(str); if (!c) return null;
    if (c.s > 0.5 && c.h >= 60 && c.h <= 105 && c.l > 0.34) return 'acc';      // lime type
    if (c.s > 0.5 && c.h >= 30 && c.h < 60 && c.l > 0.34) return 'mark';      // amber/gold type + stars
    if (c.s > 0.09 && c.h >= 78 && c.h <= 190 && c.l < 0.55) return 'quiet';   // brand-green type
    return null;
  }

  var SVG = { path: 1, circle: 1, rect: 1, polygon: 1, ellipse: 1, line: 1, polyline: 1 };

  function retint(scope) {
    scope = scope || d.body;
    markCommercial(scope);
    // Phone colour comes from section CSS. Never infer links, backgrounds or
    // border sides from transient computed styles, even on a slow CSS load.
    if (!desktopRoles.matches || !stylesReady) {
      if (!deferredDesktop.has(d.body)) deferredDesktop.add(scope);
      return;
    }
    var nodes = retintNodes(scope), ops = [], i, el, cs, tag;
    /* phase 1 — read only. A tagged role is final (re-classifying a repainted
       element would drift it); untagged elements are re-checked each pass,
       which is how late CDN paints get caught. */
    for (i = 0; i < nodes.length; i++) {
      el = nodes[i]; tag = el.tagName;
      try { cs = getComputedStyle(el); } catch (e) { continue; }
      var op = { el: el }, isCtl = false;
      var ownsColorState = !!el.closest(STATEFUL_COLORS);
      try { isCtl = el.matches(CONTROLS); } catch (e) {}
      if (!ownsColorState && !isCtl && !el.hasAttribute('data-lpl-ground')) {
        var g = groundRole(cs.backgroundColor);
        if (g) {
          op.ground = g;
          if (cs.backgroundImage && cs.backgroundImage.indexOf('gradient') > -1) op.grad = 1;
          var r = parseFloat(cs.borderTopLeftRadius);
          if (r > 2 && r < 400) op.round = 1;
        }
      }
      if (!ownsColorState && !el.hasAttribute('data-lpl-fg')) op.fg = inkRole(cs.color);
      if (!ownsColorState && !el.hasAttribute('data-lpl-bd') &&
          (cs.borderTopWidth !== '0px' || cs.borderBottomWidth !== '0px' || cs.borderLeftWidth !== '0px')) {
        op.bd = inkRole(cs.borderTopColor) || inkRole(cs.borderBottomColor) || inkRole(cs.borderLeftColor);
      }
      if (!ownsColorState && SVG[tag.toLowerCase()] && !el.hasAttribute('data-lpl-svg')) {
        if (inkRole(cs.fill)) op.svg = 'fill';
        else if (inkRole(cs.stroke)) op.svg = 'stroke';
      }
      if (tag === 'IMG' && !el.hasAttribute('data-lpl-photo') && !el.closest('.asi__img--wrapp,.header')) {
        if ((el.naturalWidth || el.width || 0) > 240) op.photo = 1;
      }
      if ((tag === 'A' || tag === 'BUTTON') && !el.hasAttribute('data-lpl-ctl') &&
          parseFloat(cs.borderTopLeftRadius) > 2) op.ctl = 1;
      if (op.ground || op.fg || op.bd || op.svg || op.photo || op.ctl) ops.push(op);
    }
    /* phase 2 — write only */
    for (i = 0; i < ops.length; i++) {
      var o = ops[i]; el = o.el;
      if (o.ground) el.setAttribute('data-lpl-ground', o.ground);
      if (o.grad) el.setAttribute('data-lpl-grad', '1');
      if (o.round) el.setAttribute('data-lpl-round', '1');
      if (o.fg) el.setAttribute('data-lpl-fg', o.fg);
      if (o.bd) el.setAttribute('data-lpl-bd', o.bd);
      if (o.svg) el.setAttribute('data-lpl-svg', o.svg);
      if (o.photo) el.setAttribute('data-lpl-photo', '1');
      if (o.ctl) el.setAttribute('data-lpl-ctl', '1');
    }
  }

  /* ── RUN ─────────────────────────────────────────────────────────────── */

  function layout() {
    syncLayout(); squareFullBleed();
  }
  function sweep() { try { retint(d.body); } catch (e) {} }

  function start() {
    deferredDesktop.add(d.body);
    // Static section styles (including media=print/onload stylesheets) settle
    // with load. Scripts injected after load must initialize immediately too.
    function ready() {
      if (stylesReady) return;
      stylesReady = true;
      layout();
      if (desktopRoles.matches) { deferredDesktop.clear(); sweep(); }
      else markCommercial(d.body);
    }
    if (d.readyState === 'complete') ready();
    else {
      syncLayout(); markCommercial(d.body);
      window.addEventListener('load', ready, { once: true });
    }
    /* new subtrees (drawers, popups, lazy sections) get tagged once, on arrival */
    var pending = new Set(), timer = 0;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].type === 'childList' && muts[i].target.nodeType === 1) {
          var targetAction = muts[i].target.closest('a,button');
          if (targetAction) pending.add(targetAction);
        }
        if (muts[i].type === 'characterData') {
          var action = muts[i].target.parentElement && muts[i].target.parentElement.closest('a,button');
          if (action) pending.add(action);
        }
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType === 1 && !SKIP[n.tagName]) pending.add(n);
        }
      }
      if (!pending.size || timer) return;
      timer = setTimeout(function () {
        var list = Array.from(pending); pending.clear(); timer = 0;
        var roots = new Set(list);
        list.forEach(function (n) {
          if (!n.isConnected) return;
          // A parent's pass already includes any children queued by the same update.
          for (var parent = n.parentElement; parent; parent = parent.parentElement) {
            if (roots.has(parent)) return;
          }
          try { retint(n); } catch (e) {}
        });
      }, 240);
    }).observe(d.body, { childList: true, characterData: true, subtree: true });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start); else start();
  desktopRoles.addEventListener('change', function () {
    if (!stylesReady || !desktopRoles.matches || !deferredDesktop.size) return;
    var list = Array.from(deferredDesktop); deferredDesktop.clear();
    var roots = new Set(list);
    list.forEach(function (node) {
      if (!node.isConnected) return;
      for (var parent = node.parentElement; parent; parent = parent.parentElement) if (roots.has(parent)) return;
      try { retint(node); } catch (e) {}
    });
  });
  var rz = 0;
  window.addEventListener('resize', function () {
    if (rz) return;
    rz = requestAnimationFrame(function () { rz = 0; syncLayout(); if (stylesReady) squareFullBleed(); });
  });
})();
